<script setup lang="ts">
import { computed, ref } from "vue";
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from "reka-ui";

import Loading from "@/components/Loading.vue";
import AccessGroup from "./AccessGroup.vue";

import { useCaseSecurityStore } from "@/stores/case-security-store";

import { $can, fromUtc } from "@/services/UtilitiesService";

import { ModuleStatus } from "@/models/ModuleStatus";

const caseSecurity = useCaseSecurityStore();

const actionKey = ref("");
const error = ref<Partial<Record<Tabs, string>>>({});

const canManageAccess = computed(() => $can("manageAccess"));

const tabs = computed(
  () =>
    [
      { key: "tags" as const, title: "Tags" },
      { key: "contributors" as const, title: "Contributors" },
      { key: "nominatedUsers" as const, title: "Nominated Users" },
    ] satisfies Array<{ key: string; title: string; hide?: boolean }>,
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const tags = computed(() => ({
  concurrency: caseSecurity.security.concurrency ?? "",
  additional: caseSecurity.security.additionalTags ?? [],
  excluded: caseSecurity.security.excludedTags ?? [],
  original: caseSecurity.security.recordTags ?? [],
}));

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const contributors = computed(() => ({
  concurrency: caseSecurity.security.concurrency ?? "",
  additional: [],
  excluded: caseSecurity.security.excludedContributors?.map((name) => ({ name })) ?? [],
  original: caseSecurity.security.recordContributors?.map((name) => ({ name })) ?? [],
}));

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const nominatedUsers = computed(() => ({
  concurrency: caseSecurity.security.concurrency ?? "",
  additional: caseSecurity.security.additionalNominatedUsers?.map((name) => ({ name })) ?? [],
  excluded: caseSecurity.security.excludedNominatedUsers?.map((name) => ({ name })) ?? [],
  original: caseSecurity.security.recordNominatedUsers?.map((name) => ({ name })) ?? [],
}));

const key = computed(() => `${actionKey.value}-${caseSecurity.security.concurrency}`);

async function runAction(group: Tabs, action: () => Promise<void>) {
  try {
    error.value[group] = undefined;
    await action();
  } catch (e) {
    error.value[group] = e as string;
  } finally {
    actionKey.value = Math.random().toString(36).slice(2);
  }
}

type Tabs = (typeof tabs)["value"][number]["key"];
</script>
<template>
  <TabsRoot
    v-if="caseSecurity.security.concurrency"
    orientation="vertical"
    :default-value="tabs[0].key"
    class="tw-flex tw-min-h-0 tw-grow"
  >
    <TabsList class="tw-flex tw-flex-col tw-gap-2 tw-overflow-y-auto tw-border-r tw-p-1 md:tw-min-w-40">
      <TabsTrigger
        v-for="tab in tabs"
        :key="tab.key"
        :value="tab.key"
        class="tw-flex tw-min-w-max tw-items-center tw-justify-start tw-rounded-md tw-border-black/20 tw-p-2 hover:tw-bg-gray-400/50 data-[state=active]:tw-border data-[state=active]:tw-bg-gray-500/20"
      >
        {{ tab.title }}
      </TabsTrigger>
    </TabsList>
    <TabsContent as-child :value="'tags' satisfies (typeof tabs)[0]['key']">
      <AccessGroup
        :key="key"
        :="tags"
        :error-message="error.tags"
        :can-add="canManageAccess"
        :can-delete="canManageAccess"
        :can-restore="canManageAccess"
        @add-entry="(name) => runAction('tags', () => caseSecurity.addEntry('tags', name))"
        @restore-entry="(name) => runAction('tags', () => caseSecurity.addEntry('tags', name))"
        @delete-entry="(name) => runAction('tags', () => caseSecurity.deleteEntry('tags', name))"
      >
        <template #columns>
          <th>Applied By</th>
          <th>Applied On</th>
        </template>
        <template #row="{ appliedBy, appliedOn }">
          <td>{{ appliedBy }}</td>
          <td>
            <time v-if="appliedOn != null" :datetime="appliedOn">{{ fromUtc(appliedOn) }}</time>
          </td>
        </template>
      </AccessGroup>
    </TabsContent>
    <TabsContent as-child :value="'contributors' satisfies (typeof tabs)[0]['key']">
      <AccessGroup
        :key="key"
        :="contributors"
        :can-add="false"
        :can-delete="canManageAccess"
        :can-restore="canManageAccess"
        :error-message="error.contributors"
        @delete-entry="(name) => runAction('contributors', () => caseSecurity.deleteEntry('contributors', name))"
        @restore-entry="(name) => runAction('contributors', () => caseSecurity.addEntry('contributors', name))"
      />
    </TabsContent>
    <TabsContent as-child :value="'nominatedUsers' satisfies (typeof tabs)[0]['key']">
      <AccessGroup
        :key="key"
        :="nominatedUsers"
        :can-add="canManageAccess"
        :can-delete="canManageAccess"
        :can-restore="canManageAccess"
        :error-message="error.nominatedUsers"
        @add-entry="(name) => runAction('nominatedUsers', () => caseSecurity.addEntry('nominatedUsers', name))"
        @restore-entry="(name) => runAction('nominatedUsers', () => caseSecurity.addEntry('nominatedUsers', name))"
        @delete-entry="(name) => runAction('nominatedUsers', () => caseSecurity.deleteEntry('nominatedUsers', name))"
      />
    </TabsContent>
  </TabsRoot>
  <div
    v-else-if="caseSecurity.moduleStatus !== ModuleStatus.Loading"
    class="tw-grid tw-size-full tw-place-items-center"
  >
    <span class="tw-text-balance"> Missing concurrency data. Could not get access data. </span>
  </div>
  <Loading v-else id="Loading Case Access Details" />
</template>
